import { Calendar, Users } from 'lucide-react'
import { Badge } from '@/components/common/Badge'
import { cn } from '@/lib/utils'
import type { Project, ProjectStatus } from '@/types'

interface ProjectCardProps {
  project: Project
  onClick?: () => void
}

const STATUS_VARIANT: Record<ProjectStatus, 'neutral' | 'info' | 'warning' | 'success' | 'danger'> = {
  DRAFT: 'neutral',
  PLANNING: 'info',
  ACTIVE: 'success',
  ON_HOLD: 'warning',
  COMPLETED: 'success',
  CANCELLED: 'danger',
}

const STATUS_LABEL: Record<ProjectStatus, string> = {
  DRAFT: 'Bozza',
  PLANNING: 'Pianificazione',
  ACTIVE: 'Attivo',
  ON_HOLD: 'In Pausa',
  COMPLETED: 'Completato',
  CANCELLED: 'Annullato',
}

// Calcola avanzamento: task se presenti, altrimenti progress manuale.
function calcProgress(project: Project): number {
  const total = project._count?.tasks ?? 0
  const done = project._count?.tasksDone ?? 0
  if (total > 0) return Math.round((done / total) * 100)
  if (project.progress != null && project.progress > 0) return project.progress
  if (project.status === 'COMPLETED') return 100
  if (project.status === 'CANCELLED') return 0
  return 0
}

function TeamAvatars({ members }: { members: Project['teamMembers'] }) {
  if (!members || members.length === 0) return null
  const visible = members.slice(0, 4)
  const extra = members.length - 4

  return (
    <div className="flex items-center">
      {visible.map((member, index) => (
        <div
          key={member.id}
          className={cn(
            'w-6 h-6 rounded-full bg-slate-200 border-2 border-white flex items-center justify-center',
            index > 0 && '-ml-2'
          )}
          title={`${member.user.firstName} ${member.user.lastName}`}
        >
          <span className="text-[9px] font-semibold text-slate-600">
            {member.user.firstName.charAt(0)}
          </span>
        </div>
      ))}
      {extra > 0 && (
        <div className="-ml-2 w-6 h-6 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center">
          <span className="text-[9px] font-semibold text-slate-500">+{extra}</span>
        </div>
      )}
    </div>
  )
}

export function ProjectCard({ project, onClick }: ProjectCardProps) {
  const progress = calcProgress(project)
  const isOverdue =
    project.endDate &&
    new Date(project.endDate) < new Date() &&
    project.status !== 'COMPLETED' &&
    project.status !== 'CANCELLED'

  return (
    <div
      className={cn(
        'bg-white rounded-2xl border border-slate-200 shadow-card p-4',
        'hover:border-hairline-strong transition-colors',
        onClick && 'cursor-pointer'
      )}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => e.key === 'Enter' && onClick() : undefined}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-slate-900 leading-snug break-words line-clamp-2">
            {project.name}
          </h3>
          {project.client && (
            <p className="text-xs text-slate-500 mt-0.5 truncate">{project.client.name}</p>
          )}
        </div>
        <Badge variant={STATUS_VARIANT[project.status]} size="sm" className="flex-shrink-0">
          {STATUS_LABEL[project.status]}
        </Badge>
      </div>

      {/* Description */}
      {project.description && (
        <p className="text-xs text-slate-500 mb-3 line-clamp-2">{project.description}</p>
      )}

      {/* Progress bar */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-slate-500 tabular-nums">
            {(project._count?.tasks ?? 0) > 0
              ? `${project._count?.tasksDone ?? 0}/${project._count?.tasks} task`
              : 'Avanzamento'}
          </span>
          <span className="text-xs font-medium text-slate-700 tabular-nums">{progress}%</span>
        </div>
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div
            className={cn(
              'h-full rounded-full transition-all',
              isOverdue
                ? 'bg-danger-500'
                : project.status === 'COMPLETED'
                ? 'bg-success-500'
                : project.status === 'ACTIVE'
                ? 'bg-success-500'
                : project.status === 'CANCELLED'
                ? 'bg-slate-300'
                : project.status === 'ON_HOLD'
                ? 'bg-warning-500'
                : project.status === 'PLANNING'
                ? 'bg-primary-500'
                : 'bg-slate-400'
            )}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Footer: PM + team + deadline */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          {project.manager && (
            <span className="text-[10px] text-slate-500 truncate" title={`PM: ${project.manager.firstName} ${project.manager.lastName}`}>
              PM: {project.manager.firstName} {project.manager.lastName?.charAt(0)}.
            </span>
          )}
          <TeamAvatars members={project.teamMembers} />
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {project.teamMembers && project.teamMembers.length > 0 && (
            <div className="flex items-center gap-1 text-xs text-slate-500 tabular-nums">
              <Users className="w-3 h-3" />
              {project.teamMembers.length}
            </div>
          )}
          {project.endDate && (
            <div
              className={cn(
                'flex items-center gap-1 text-xs tabular-nums',
                isOverdue ? 'text-danger-600' : 'text-slate-500'
              )}
            >
              <Calendar className="w-3 h-3" />
              {new Date(project.endDate).toLocaleDateString('it-IT', {
                day: '2-digit',
                month: 'short',
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
